// Helpers shared by the report and dashboard InsightsView builders. Reports and
// dashboards describe their gadgets to the agent identically — same metric/
// dimension/trend derivation, same filter-to-clause mapping, same caps — they
// differ only in where the scope comes from (a report has one shared scope; a
// dashboard resolves one per gadget). Keeping the shared logic here stops the two
// builders drifting apart.

import { getDrillDownDescription } from 'components/FilterPlates/FilterPlate';
import filterTypeCheckers from 'components/FilterPlateForm/filterTypeCheckers';
import { getFieldLabel } from 'hooks/useDrillDownFieldName';
import {
  describeDateOffset,
  toSeriesName,
} from 'hooks/useV5ChartData/queryOverrides/describeQueryOverrides';
import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import visTypeCheckers from 'types/visTypeCheckers';

import {
  InsightsViewDrillTarget,
  InsightsViewFilter,
  InsightsViewFilterClause,
  InsightsViewSeriesOverride,
} from 'contextProviders/InsightsContentProvider/viewTypes';

// The trend buckets aggregate_metric can actually reproduce (its
// trendByCalendarInterval enum). Anything else is real on screen but not
// re-queryable, so the agent must be told not to trust a recomputation.
const REPRODUCIBLE_INTERVALS = new Set([
  'year',
  'quarter',
  'month',
  'week',
  'day',
]);

// Content-level caps. The server applies its own token budget on top; these keep
// the payload sane before it ever leaves the browser.
export const MAX_VIEW_GADGETS = 8;
export const MAX_VIEW_FILTERS = 12;

export type BaseViewsLookup = {
  [dataset: string]: FleetOps.BaseView | undefined;
};
export type MetricOptionsLookup = {
  [metricId: string]: Metrics.Metric | undefined;
};

// Translates a resolved filter into the agent's AND-combined tool clauses. An
// EMPTY list means the filter has no metric-query equivalent — that absence is
// what sets hasUnreproducibleFilters. Most filters yield one clause; a text
// filter that also excludes Blanks yields two (a value clause + a presence
// clause). Mirrors the gateway keyword resolver (graphql src/query/index.js)
// and the app's own src/utils/buildFilterInput.
export const toClause = (filter: FixedFilter): InsightsViewFilterClause[] => {
  if (filterTypeCheckers.isWildcardFilter(filter)) {
    return [];
  }
  if (filterTypeCheckers.isDateFilter(filter)) {
    // A date drill-down is already folded into dateScope; expressing it again as
    // a clause would double-apply it.
    return [];
  }
  if (filterTypeCheckers.isTextFilter(filter)) {
    return textFilterClauses(filter);
  }
  if (filterTypeCheckers.isNumberFilter(filter)) {
    const range = filter.rangeValue;
    if (!range) {
      // "has some value" / "has no value" — no range equivalent.
      return [];
    }
    const operator = (() => {
      switch (range.operator) {
        case 'is more than or equal to':
          return 'gte';
        case 'is more than':
          return 'gt';
        case 'is less than':
          return 'lt';
        case 'is less than or equal to':
          return 'lte';
        case 'is between':
          return 'between';
        default:
          return undefined;
      }
    })();
    if (!operator) {
      return [];
    }
    const values =
      operator === 'between' && range.valueB !== undefined
        ? [range.valueA, range.valueB]
        : [range.valueA];
    return [{ field: filter.field, kind: 'number', operator, values }];
  }
  if (filterTypeCheckers.isBooleanFilter(filter)) {
    if (filter.booleanValue === undefined) {
      return [];
    }
    return [
      {
        field: filter.field,
        kind: 'boolean',
        operator: filter.isExclude ? 'is not' : 'is',
        values: [filter.booleanValue],
      },
    ];
  }
  return [];
};

// Text filters, including the "Blanks" mechanism (keywordsExists). The polarity
// here matches the gateway keyword resolver exactly:
//  - keywordsExists === false means Blanks are part of the value set.
//  - A pure-blank filter (no values) is a single presence clause.
//  - "excluding Blanks, X, Y" (exists === false, isExclude) drops blanks AND the
//    values → an 'is not' clause plus an 'is not blank' clause.
//  - "is Blanks, X, Y" (exists === false, not excluding) is an OR of blank/value
//    the tool can't express as AND clauses → not reproducible.
const textFilterClauses = (filter: TextFilter): InsightsViewFilterClause[] => {
  const { field, keywordValues, keywordsExists, isExclude } = filter;
  const values = keywordValues || [];

  if (keywordsExists === undefined) {
    if (values.length === 0) {
      return [];
    }
    return [
      { field, kind: 'text', operator: isExclude ? 'is not' : 'is', values },
    ];
  }

  // Pure blank filter — no values, just a presence check.
  if (values.length === 0) {
    const keepOnlyBlanks =
      (keywordsExists === false && !isExclude) ||
      (keywordsExists === true && isExclude);
    return [
      {
        field,
        kind: 'text',
        operator: keepOnlyBlanks ? 'is blank' : 'is not blank',
        values: [],
      },
    ];
  }

  if (keywordsExists === false) {
    // "blank OR value" — only the exclusion form is a pure AND we can express.
    if (!isExclude) {
      return [];
    }
    return [
      { field, kind: 'text', operator: 'is not', values },
      { field, kind: 'text', operator: 'is not blank', values: [] },
    ];
  }

  // keywordsExists === true with values: the gateway treats it as a plain
  // keyword filter (blanks aren't in the set), so a single value clause.
  return [
    { field, kind: 'text', operator: isExclude ? 'is not' : 'is', values },
  ];
};

export const toViewFilter = ({
  filter,
  baseViews,
}: {
  filter: FixedFilter;
  baseViews: BaseViewsLookup;
}): InsightsViewFilter => {
  const clauses = toClause(filter);
  return {
    description: getDrillDownDescription(
      filter,
      getFieldLabel({
        field: filter.field,
        dataset: filter.dataset,
        baseViews,
      }),
    ),
    ...(clauses.length ? { clauses } : {}),
    // Legacy singular mirror for older servers (see viewTypes).
    ...(clauses.length === 1 ? { clause: clauses[0] } : {}),
  };
};

// A series' own filter plate. Only fixed plates map onto clauses; the rest are
// named, with none, so the agent knows it can't reproduce them.
const plateToViewFilter = ({
  plate,
  baseViews,
}: {
  plate: FilterPlateType;
  baseViews: BaseViewsLookup;
}): InsightsViewFilter => {
  if (filterPlateTypeCheckers.isFixed(plate)) {
    return toViewFilter({ filter: plate.fixedValue, baseViews });
  }
  if (filterPlateTypeCheckers.isExcludeTermedDrivers(plate)) {
    return { description: 'excluding termed drivers' };
  }
  return {
    description: filterPlateTypeCheckers.isSavedFilter(plate)
      ? 'a saved filter'
      : 'a variable filter',
  };
};

// The series of a V5 chart that ask their own question (see
// V5SeriesQueryOverrides), as the agent re-queries them
export const describeSeriesOverrides = ({
  chartDef,
  metricOptionsLookup,
  baseViews,
}: {
  chartDef: VisualisationDefinition;
  metricOptionsLookup: { [metricId: string]: Metrics.Metric | undefined };
  baseViews: BaseViewsLookup;
}): InsightsViewSeriesOverride[] => {
  if (!visTypeCheckers.isV5ChartDef(chartDef)) {
    return [];
  }
  return chartDef.series.flatMap((series): InsightsViewSeriesOverride[] => {
    const overrides = series.queryOverrides;
    if (!hasQueryOverrides(overrides)) {
      return [];
    }
    const metric = metricOptionsLookup[series.metricId];
    const { isFiltersOverridden, filters, dateOffset, dateField } = overrides;
    return [
      {
        metric: {
          id: series.metricId,
          ...(metric ? { name: metric.name } : {}),
        },
        label: toSeriesName({
          metricName: metric ? metric.name : series.metricId,
          series,
        }),
        ...(isFiltersOverridden ? { ignoresFilters: true } : {}),
        ...(filters && filters.length > 0
          ? {
              filters: filters.map((plate) =>
                plateToViewFilter({ plate, baseViews }),
              ),
            }
          : {}),
        ...(dateOffset && dateOffset.count > 0
          ? {
              period: `${describeDateOffset(dateOffset)} (relative to the gadget's dates)`,
            }
          : {}),
        ...(dateField ? { dateField } : {}),
      },
    ];
  });
};

// Business-language label for the trend/grouping, plus whether the agent could
// reproduce it. Only V5 chart defs carry these.
export const describeTrend = (
  chartDef: V5ChartDefinition,
): { trend?: string; isTrendReproducible?: boolean } => {
  if (chartDef.trendByHourInterval) {
    return { trend: 'hour of day', isTrendReproducible: false };
  }
  if (chartDef.groupByDayOfWeek) {
    return { trend: 'day of week', isTrendReproducible: false };
  }
  if (chartDef.groupByMonth) {
    return { trend: 'month of year', isTrendReproducible: false };
  }
  if (chartDef.trendByFiscalInterval) {
    return {
      trend: `fiscal ${chartDef.trendByFiscalInterval}`,
      isTrendReproducible: false,
    };
  }
  if (chartDef.trendByFixedIntervalDays) {
    return {
      trend: `${chartDef.trendByFixedIntervalDays}-day buckets`,
      isTrendReproducible: false,
    };
  }
  if (chartDef.trendByCalendarInterval) {
    return {
      trend: chartDef.trendByCalendarInterval,
      isTrendReproducible: REPRODUCIBLE_INTERVALS.has(
        chartDef.trendByCalendarInterval,
      ),
    };
  }
  return {};
};

// The groupBy field for gadget types that aren't V5 chart defs. Mirrors
// chartDefDimensionField in src/hooks/useFilterInput.ts.
export const nonV5DimensionField = (
  chartDef: ExtendedVisualisationDefinition,
): string | undefined => {
  if (visTypeCheckers.isGauge(chartDef)) {
    return chartDef.peerGroup;
  }
  if (visTypeCheckers.isRankingMatrix(chartDef)) {
    return chartDef.groupByField;
  }
  if (visTypeCheckers.isSingleMetricDateMatrix(chartDef)) {
    return chartDef.groupByField;
  }
  if (visTypeCheckers.isPaceMatrix(chartDef)) {
    return chartDef.groupByField;
  }
  return undefined;
};

// The dimensionA/dimensionB pair a gadget slices by, humanised. Works for both V5
// chart defs (explicit dimensionA/B) and the non-V5 vis types (a single groupBy
// field).
export const buildDimensions = ({
  chartDef,
  baseViews,
}: {
  chartDef: ExtendedVisualisationDefinition;
  baseViews: BaseViewsLookup;
}): {
  dimensionA?: { field: string; label: string };
  dimensionB?: { field: string; label: string };
} => {
  const toDimension = (dimension: Dimension | undefined) =>
    dimension
      ? {
          field: dimension.field,
          label: getFieldLabel({ field: dimension.field, baseViews }),
        }
      : undefined;

  const isV5 = visTypeCheckers.isV5ChartDef(chartDef);
  const dimensionA = isV5
    ? toDimension(chartDef.dimensionA)
    : (() => {
        const field = nonV5DimensionField(chartDef);
        return field
          ? { field, label: getFieldLabel({ field, baseViews }) }
          : undefined;
      })();
  const dimensionB = isV5 ? toDimension(chartDef.dimensionB) : undefined;

  return {
    ...(dimensionA ? { dimensionA } : {}),
    ...(dimensionB ? { dimensionB } : {}),
  };
};

// Where a click on a value navigates, for anything DrillDownable (dashboard
// gadgets, metric-list items, scorecard/goal KPIs). A report link wins over a
// board link, matching usePopupCallback's precedence. The name is a courtesy for
// the prose — the id is what get_content_definition needs.
export const buildDrillTarget = ({
  reportDrillDownId,
  boardDrillDownId,
  reportNamesById,
  boardNamesById,
}: {
  reportDrillDownId?: string;
  boardDrillDownId?: string;
  reportNamesById: { [id: string]: string | undefined };
  boardNamesById: { [id: string]: string | undefined };
}): InsightsViewDrillTarget | undefined => {
  if (reportDrillDownId) {
    const name = reportNamesById[reportDrillDownId];
    return { type: 'report', id: reportDrillDownId, ...(name ? { name } : {}) };
  }
  if (boardDrillDownId) {
    const name = boardNamesById[boardDrillDownId];
    return { type: 'board', id: boardDrillDownId, ...(name ? { name } : {}) };
  }
  return undefined;
};

// Every dataset a metric reads, following compound metrics down to their
// components. Used to decide whether invisible default dataset filters apply.
//
// DISTINCT. `seen` de-duplicates metric IDS, which is a different thing: a
// compound whose components sit on the same dataset — "Working %" is working
// days over total days, both driverDays — would otherwise return that dataset
// once per component and read as "driverDays, driverDays".
const collectDatasets = ({
  metricId,
  metricOptionsLookup,
  seen,
}: {
  metricId: string;
  metricOptionsLookup: MetricOptionsLookup;
  seen: Set<string>;
}): string[] => {
  if (seen.has(metricId)) {
    return [];
  }
  seen.add(metricId);
  const metric = metricOptionsLookup[metricId];
  if (!metric) {
    return [];
  }
  if (metric.type === 'compound') {
    return metric.metricIds.flatMap((id) =>
      collectDatasets({ metricId: id, metricOptionsLookup, seen }),
    );
  }
  return metric.dataType ? [metric.dataType] : [];
};

export const datasetsForMetric = ({
  metricId,
  metricOptionsLookup,
}: {
  metricId: string;
  metricOptionsLookup: MetricOptionsLookup;
}): string[] => [
  ...new Set(
    collectDatasets({
      metricId,
      metricOptionsLookup,
      seen: new Set<string>(),
    }),
  ),
];
